"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'nav_home', label: 'Home', href: '/homepage' },
    { id: 'nav_about', label: 'About', href: '/about' },
    { id: 'nav_products', label: 'Products', href: '/products' },
    { id: 'nav_why', label: 'Why Choose Us', href: '/why-choose-us' },
    { id: 'nav_compliance', label: 'Compliance', href: '/compliance-certifications' },
    { id: 'nav_contact', label: 'Contact', href: '/contact' },
  ];

  return (
    <header className="fixed top-0 left-0 w-full z-50 transition-all duration-300 px-6 py-4">
      <nav
        className={`max-w-7xl mx-auto flex items-center justify-between rounded-2xl px-6 py-3 shadow-lg transition-all duration-300 ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-md border border-border' :'bg-white/10 backdrop-blur-md border border-white/20'
        }`}
      >
        {/* Logo */}
        <Link href="/homepage" className="flex items-center space-x-2">
          <Image 
            src="/assets/images/Black_Gold_Elegant_Luxury_Royal_Diamond_Brand_Logo-1770628495800.png" 
            alt="Fast Minerals East Africa Logo" 
            width={40} 
            height={40} 
            className="rounded-lg object-contain"
          />
          <span className="font-bold text-xl tracking-tight text-foreground">
            Fast Minerals
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks?.map((link) => (
            <Link
              key={link?.id}
              href={link?.href}
              className={`text-sm font-medium transition-colors ${
                pathname === link?.href
                  ? 'text-primary font-bold' :'text-muted hover:text-primary'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </div>

        {/* Contact Info */}
        <div className="hidden xl:flex items-center gap-4 text-sm">
          <a href="mailto:fastminerals9@gmail.com" className="flex items-center gap-2 text-muted hover:text-primary transition-colors">
            <Icon name="EnvelopeIcon" size={16} />
            <span className="font-medium">fastminerals9@gmail.com</span>
          </a>
          <a href="tel:+14378723940" className="flex items-center gap-2 text-muted hover:text-primary transition-colors">
            <Icon name="PhoneIcon" size={16} />
            <span className="font-medium">+1 437 872 3940</span>
          </a>
        </div>

        {/* CTA Buttons */}
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-2">
            <Link
              href="/contact"
              className="px-5 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-full hover:scale-105 transition-transform shadow-md"
            >
              Request Quote
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-foreground"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <Icon name="XMarkIcon" size={24} />
            ) : (
              <Icon name="Bars3Icon" size={24} />
            )}
          </button>
        </div>
      </nav>
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden mt-4 bg-white border border-border rounded-2xl p-6 shadow-2xl mx-6 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex flex-col gap-4">
            {navLinks?.map((link) => (
              <Link
                key={link?.id}
                href={link?.href}
                onClick={() => setIsMenuOpen(false)}
                className={`text-lg font-medium ${
                  pathname === link?.href
                    ? 'text-primary font-bold' :'text-foreground hover:text-primary'
                }`}
              >
                {link?.label}
              </Link>
            ))}
            <hr className="border-border my-2" />
            <Link
              href="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="w-full py-3 text-center font-medium text-primary-foreground bg-primary rounded-xl"
            >
              Request Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}